import { Node, mergeAttributes, InputRule, PasteRule } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { VariableTagView } from '../components/VariableTagView';

/**
 * VariableTag — An inline, atomic (non-editable) node that represents
 * a data placeholder like <<Borrower Name>> or <<**Borrower Name**>> inside the document.
 */
export const VariableTag = Node.create({
    name: 'variableTag',
    group: 'inline',
    inline: true,
    atom: true,
    draggable: true,
    marks: '_',
    addAttributes() {
        return {
            variableKey: {
                default: '',
                parseHTML: (element) => (element.getAttribute('data-variable-key') || '').replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#39;|&apos;/g, "'"),
                renderHTML: (attributes) => ({
                    'data-variable-key': attributes.variableKey,
                }),
            },
            label: {
                default: '',
                parseHTML: (element) => (element.getAttribute('data-label') || '').replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#39;|&apos;/g, "'"),
                renderHTML: (attributes) => ({
                    'data-label': attributes.label,
                }),
            },
        };
    },
    parseHTML() {
        return [{ tag: 'span[data-variable-tag]' }];
    },
    renderHTML({ HTMLAttributes }) {
        return [
            'span',
            mergeAttributes({ 'data-variable-tag': '', class: 'variable-tag' }, HTMLAttributes),
            `<<${HTMLAttributes['data-label'] || ''}>>`,
        ];
    },
    addNodeView() {
        return ReactNodeViewRenderer(VariableTagView);
    },
    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey('variableSelectionDecoration'),
                props: {
                    decorations(state) {
                        const { from, to, empty } = state.selection;
                        if (empty) return DecorationSet.empty;
                        const decos = [];
                        state.doc.nodesBetween(from, to, (node, pos) => {
                            if (node.type.name === 'variableTag') {
                                decos.push(
                                    Decoration.node(pos, pos + node.nodeSize, {
                                        class: 'is-selected-in-range',
                                    })
                                );
                            }
                        });
                        return DecorationSet.create(state.doc, decos);
                    },
                },
            }),
        ];
    },
    addInputRules() {
        return [
            new InputRule({
                find: /(?:\*\*<<|<<\*\*|<<|«\*\*|«)([^<>«»]+)(?:\*\*>>|>>\*\*|>>|»\*\*|»)$/,
                handler: ({ state, range, match }) => {
                    const { tr } = state;
                    const start = range.from;
                    const end = range.to;
                    const fullMatch = match[0] || '';
                    const isBold = fullMatch.includes('**');
                    const name = match[1].replace(/\*\*/g, '').trim();
                    if (!name) return;
                    const key = name.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
                    const marks = isBold && state.schema.marks.bold ? [state.schema.marks.bold.create()] : [];
                    const node = this.type.create({
                        variableKey: key,
                        label: name,
                    }, null, marks);
                    tr.replaceWith(start, end, node);
                    tr.scrollIntoView();
                },
            }),
        ];
    },
    addPasteRules() {
        return [
            new PasteRule({
                find: /(?:\*\*<<|<<\*\*|<<|«\*\*|«)([^<>«»]+)(?:\*\*>>|>>\*\*|>>|»\*\*|»)/g,
                handler: ({ state, range, match }) => {
                    const { tr } = state;
                    const start = range.from;
                    const end = range.to;
                    const fullMatch = match[0] || '';
                    const isBold = fullMatch.includes('**');
                    const name = match[1].replace(/\*\*/g, '').trim();
                    if (!name) return;
                    const key = name.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
                    const marks = isBold && state.schema.marks.bold ? [state.schema.marks.bold.create()] : [];
                    const node = this.type.create({
                        variableKey: key,
                        label: name,
                    }, null, marks);
                    tr.replaceWith(start, end, node);
                },
            }),
        ];
    },
});
